import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { getJSON } from '@/lib/api'
import { EventOrderContent, type OrderLinkResponse } from '@/pages/OrderLinkPage'

type EventOrderSummary = {
  id: number
  status: string
  event: { name: string; date: string }
}

type EventOrdersResponse = {
  upcoming: EventOrderSummary[]
  past: EventOrderSummary[]
}

export function MemberPortalEvents() {
  const orders = useQuery({
    queryKey: ['member-portal-orders'],
    queryFn: () => getJSON<EventOrdersResponse>('/api/v1/member_portal/orders'),
    retry: false,
  })

  if (orders.isLoading) {
    return (
      <p className="mt-6 text-body-sm text-muted-foreground" role="status">
        Loading Event Orders…
      </p>
    )
  }

  if (orders.isError || !orders.data) {
    return <Alert className="mt-6">Event Orders are unavailable. Please try again.</Alert>
  }

  if (orders.data.upcoming.length === 0 && orders.data.past.length === 0) {
    return (
      <Card className="mt-6">
        <CardContent className="p-4">
          <p className="text-body-sm font-medium">No Event Orders yet</p>
          <p className="mt-1 text-caption text-muted-foreground">
            Event Orders you place will appear here.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="mt-6 space-y-8">
      <EventOrderGroup title="Upcoming" orders={orders.data.upcoming} />
      <EventOrderGroup title="Past" orders={orders.data.past} />
    </div>
  )
}

export function MemberPortalEventOrder({ orderId }: { orderId: number }) {
  const queryKey = ['member-portal-order', orderId] as const
  const order = useQuery({
    queryKey,
    queryFn: () => getJSON<OrderLinkResponse>(`/api/v1/member_portal/orders/${orderId}`),
    retry: false,
  })

  if (order.isLoading) {
    return (
      <p className="text-body-sm text-muted-foreground" role="status">
        Loading Event Order…
      </p>
    )
  }

  if (order.isError || !order.data) {
    return <Alert>Event Order unavailable.</Alert>
  }

  return (
    <EventOrderContent
      order={order.data.order_link}
      queryKey={queryKey}
      attendeePath={(seat) => `/api/v1/member_portal/orders/${orderId}/attendees/${seat}`}
    />
  )
}

function EventOrderGroup({ title, orders }: { title: string; orders: EventOrderSummary[] }) {
  if (orders.length === 0) return null

  return (
    <section className="space-y-3">
      <h2 className="text-body font-semibold">{title}</h2>
      <div className="divide-y divide-border border-y border-border">
        {orders.map((order) => (
          <Link
            key={order.id}
            to={`/portal/events/${order.id}`}
            className="flex items-center justify-between gap-4 py-3 text-body-sm hover:bg-muted/50"
          >
            <span className="min-w-0">
              <span className="block truncate font-medium">{order.event.name}</span>
              <span className="mt-0.5 block text-caption text-muted-foreground">
                {order.event.date}
              </span>
            </span>
            <Badge variant={order.status === 'cancelled' ? 'neutral' : 'success'}>
              {humanizeStatus(order.status)}
            </Badge>
          </Link>
        ))}
      </div>
    </section>
  )
}

function humanizeStatus(status: string) {
  return status.replaceAll('_', ' ').replace(/^./, (first) => first.toUpperCase())
}
